import BlockContent from '@sanity/block-content-to-react';
import React, { useState } from 'react';

import { useTranslation } from 'react-i18next';
import Image from './components/ui/Image';
import ListItem from './components/ui/ListItem';
import LocalizedLink from './components/ui/LocalizedLink';
import TextLink from './components/ui/TextLink';
import Video from './components/ui/Video';
import VideoTranscriptionModal from './components/ui/VideoTranscriptionModal';
import { SanityImageType } from './graphql-fragments/SanityImage';
import TranscriptIcon from './images/transcript.inline.svg';
import * as styles from './serializers.module.scss';
import { AreaOfExpertise } from './templates/AreaOfExpertisePage';
import { Service } from './templates/ServicePage';
import { PageDocumentType, RawPortableText, VersatileLink } from './types/types';
import { getReferenceUrl } from './utils/projectUtils';
import { getPageDocumentUrl, getUrlFromVersatileLink } from './utils/sanity';
import { clsx, slugify } from './utils/utils';

export const InternalLinkSerializer = (props: {
  mark: {
    pageReference: {
      slug: { current: string };
      _type: PageDocumentType;
    };
    anchorLink?: string;
  };
  children: React.ReactNode;
}): React.ReactElement => {
  return (
    <TextLink to={getPageDocumentUrl(props.mark.pageReference, props.mark.anchorLink)}>
      {props.children}
    </TextLink>
  );
};

const ExternalLinkSerializer = (props: {
  mark: {
    url: string;
  };
  children: React.ReactNode;
}): React.ReactElement => {
  return <TextLink to={props.mark.url}>{props.children}</TextLink>;
};

const BlockSerializer = (props: {
  node: {
    children: Array<{
      text: string;
    }>;
    style?: string;
  };
  children: React.ReactNode;
}): React.ReactElement => {
  const slug = slugify(props.node.children.map(child => child.text).join(''));
  if (props.node.style === 'h2') {
    return <h2 id={slug}>{props.children}</h2>;
  }
  if (props.node.style === 'h3') {
    return <h3 id={slug}>{props.children}</h3>;
  }
  if (props.node.style === 'h4') {
    return <h4 id={slug}>{props.children}</h4>;
  }
  if (props.node.style === 'h5') {
    return <h5 id={slug}>{props.children}</h5>;
  }

  // Handle normal paragraphs (including empty ones)
  if (!props.node.style || props.node.style === 'normal') {
    const text = props.node.children.map(child => child.text).join('');
    // Render empty paragraphs as non-breaking space to preserve spacing
    // Use children prop to preserve text formatting (bold, italic, etc.)
    return text ? (
      <p>{props.children}</p>
    ) : (
      <p className={styles.emptyParagraph}>{'\u00A0'}</p>
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-call
  return BlockContent.defaultSerializers.types.block(props);
};

const ListSerializer = (props: {
  type: string;
  level: number;
  children: React.ReactNode;
}): React.ReactElement => {
  if (props.type === 'number' && props.level === 2) {
    return <ol type="a">{props.children}</ol>;
  }
  // eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-call
  return BlockContent.defaultSerializers.list(props);
};

const ListItemsSerializer = (props: {
  node: {
    items: Array<{
      title: string;
      text: string;
    }>;
  };
}): React.ReactElement => {
  return (
    <ul>
      {props.node.items.map((item, index) => (
        <ListItem
          listType="withBullet"
          text={item.text}
          horizontalLayout
          title={item.title}
          key={index}
          className={styles.listItem}
        />
      ))}
    </ul>
  );
};

const CustomListSerializer = (props: {
  node: {
    prepopulateContentWith?: 'areasOfExpertise' | 'services' | 'none';
    listItems: Array<{
      title?: string;
      text: RawPortableText;
    }>;
    listType: 'simple' | 'numbered' | 'grid';
  };
  serializerContext: {
    areasOfExpertise: Array<AreaOfExpertise>;
    services: Array<Service>;
  };
}): React.ReactElement => {
  const { areasOfExpertise, services } = props.serializerContext;

  const renderContent = item => {
    if (props.node.prepopulateContentWith === 'areasOfExpertise') {
      return <p>{item.hero?.text}</p>;
    } else if (props.node.prepopulateContentWith === 'services') {
      return <p>{item.description}</p>;
    } else {
      return (
        <div>
          <BlockContent
            blocks={item.text}
            renderContainerOnSingleChild
            serializers={serializers}
            className={styles.itemText}
          />
        </div>
      );
    }
  };

  const ListElType = props.node.listType === 'numbered' ? 'ol' : 'ul';
  return (
    <ListElType
      className={clsx(
        styles.list,
        props.node.listType === 'numbered' && styles.numberedList,
        props.node.listType === 'grid' && styles.gridList,
        props.node.listType === 'simple' && styles.simpleList,
      )}
    >
      {(props.node.prepopulateContentWith && props.node.prepopulateContentWith !== 'none'
        ? props.node.prepopulateContentWith === 'areasOfExpertise'
          ? areasOfExpertise
          : services
        : props.node.listItems
      ).map((item, index) => (
        <li key={index} className={styles.listItem}>
          {props.node.listType === 'grid' && <div className={styles.line}></div>}
          {props.node.listType === 'numbered' && (
            <div className={styles.numberAndLine}>
              <div className={styles.numberWrapper}>
                <span className={styles.numberContainer}>{index + 1}</span>
              </div>
              <div className={styles.line}></div>
            </div>
          )}
          <div className={styles.textContainer}>
            {item.title && <h3 className={styles.itemTitle}>{item.title}</h3>}
            {renderContent(item)}
          </div>
        </li>
      ))}
    </ListElType>
  );
};

const VideoSerializer = (props: {
  node: {
    url: string;
    transcription?: {
      videoTitle?: string;
      videoSubtitle?: string;
      videoTranscription: string;
    };
  };
}): React.ReactElement => {
  const { t } = useTranslation();

  const [isVideoTranscriptionModalOpen, setIsVideoTranscriptionModalOpen] = useState(false);

  return (
    <div className={styles.videoContainer}>
      <Video url={props.node.url} />
      {props.node.transcription?.videoTranscription && (
        <>
          <button
            onClick={() => {
              setIsVideoTranscriptionModalOpen(true);
            }}
            className={styles.videoTranscriptionLink}
          >
            {t('hero.button_video_transcript', 'Ver transcrição do vídeo')}{' '}
            <TranscriptIcon className={styles.linkIcon} />
          </button>
          <VideoTranscriptionModal
            isOpen={isVideoTranscriptionModalOpen}
            title={props.node.transcription.videoTitle}
            subtitle={props.node.transcription.videoSubtitle}
            text={props.node.transcription.videoTranscription}
            onClose={() => {
              setIsVideoTranscriptionModalOpen(false);
            }}
          />
        </>
      )}
    </div>
  );
};

const LogoImageSerializer = (props: {
  node: { logo: SanityImageType; title?: string };
}): React.ReactElement => {
  return (
    <div className={styles.logoImageContainer}>
      {props.node.title && <span className={styles.logoTitle}>{props.node.title}</span>}
      <div className={styles.logoContainer}>
        <Image image={props.node.logo} className={styles.logo} />
      </div>
    </div>
  );
};

const LinksGridSerializer = (props: {
  node: { title: string; links: Array<VersatileLink & { text: string }> };
}): React.ReactElement => {
  return (
    <div className={styles.linksGridContainer}>
      {props.node.title && <span className={styles.logoTitle}>{props.node.title}</span>}
      <div className={styles.linksContainer}>
        {props.node.links.map((link, index) => (
          <TextLink
            key={index}
            to={getUrlFromVersatileLink(link)}
            className={styles.link}
            withArrowIcon
          >
            {link.text}
          </TextLink>
        ))}
      </div>
    </div>
  );
};

const GenericLinkSerializer = (props: {
  mark: {
    url: string;
  };
  children: React.ReactNode;
}): React.ReactElement => {
  return <a href={props.mark.url}>{props.children}</a>;
};

const EmbeddedImageSerializer = (props: {
  node: {
    image: {
      asset: {
        originalFilename: string;
        url: string;
      };
    };
    imageWidth: number;
  };
}): React.ReactElement => {
  return (
    <div className={styles.embbededImageWrapper}>
      <img
        src={props.node.image.asset.url}
        alt={props.node.image.asset.originalFilename}
        style={{ width: props.node.imageWidth || '100%' }}
      />
    </div>
  );
};

const InsightLinkSerializer = (props: {
  mark: {
    insight: {
      slug: { current: string };
    };
  };
  children: React.ReactNode;
}): React.ReactElement => {
  return (
    <LocalizedLink to={getReferenceUrl('SanityInsight', props.mark.insight.slug.current)}>
      {props.children}
    </LocalizedLink>
  );
};

const FundLinkSerializer = (props: {
  mark: {
    fund: {
      slug: { current: string };
      internal: { type: 'SanityAlternativeFund' | 'SanitySecurityFund' };
    };
  };
  children: React.ReactNode;
}): React.ReactElement => {
  return props.mark.fund.internal?.type === 'SanityAlternativeFund' ? (
    <LocalizedLink to={getReferenceUrl('SanityAlternativeFund', props.mark.fund.slug.current)}>
      {props.children}
    </LocalizedLink>
  ) : (
    <LocalizedLink to={getReferenceUrl('SanitySecurityFund', props.mark.fund.slug.current)}>
      {props.children}
    </LocalizedLink>
  );
};

export const getSerializers = (context?: any) => ({
  marks: {
    internalLink: props => <InternalLinkSerializer {...props} serializerContext={context} />,
    externalLink: props => <ExternalLinkSerializer {...props} serializerContext={context} />,
    genericLink: props => <GenericLinkSerializer {...props} serializerContext={context} />,
    fundLink: props => <FundLinkSerializer {...props} serializerContext={context} />,
    insightLink: props => <InsightLinkSerializer {...props} serializerContext={context} />,
  },
  types: {
    video: props => <VideoSerializer {...props} serializerContext={context} />,
    block: props => <BlockSerializer {...props} serializerContext={context} />,
    listItems: props => <ListItemsSerializer {...props} serializerContext={context} />,
    customList: props => <CustomListSerializer {...props} serializerContext={context} />,
    logoImage: props => <LogoImageSerializer {...props} serializerContext={context} />,
    linksGrid: props => <LinksGridSerializer {...props} serializerContext={context} />,
    embeddedImage: props => <EmbeddedImageSerializer {...props} serializerContext={context} />,
  },
  list: props => <ListSerializer {...props} serializerContext={context} />,
});

const serializers = getSerializers();

export default serializers;
